import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

const DeleteProjectModal = ({ project, onClose, onProjectDeleted }) => {
  const { instance, accounts } = useMsal();
  const [confirmationText, setConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    if (confirmationText.toLowerCase() !== 'delete') {
      setError('Please type "delete" to confirm');
      return;
    }

    setIsDeleting(true);
    setError('');

    try {
      await authenticatedApiCall(
        instance,
        accounts,
        'DELETE',
        `/projects/${project.id}`
      );

      onProjectDeleted(project.id);
      onClose();
    } catch (error) {
      console.error('Error deleting project:', error);
      setError(error.response?.data?.message || 'Failed to delete project');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setConfirmationText('');
    setError('');
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '2rem',
        maxWidth: '500px',
        width: '90%',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <h3 style={{ 
          margin: '0 0 1rem 0', 
          color: '#dc3545',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          ⚠️ Delete Project
        </h3>
        
        <div style={{ marginBottom: '1.5rem' }}>
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            You are about to permanently delete the project:
          </p>
          <div style={{
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            border: '1px solid #dee2e6'
          }}>
            <strong>Job Number:</strong> {project.job_number}<br />
            <strong>Job Name:</strong> {project.job_name}
          </div>
        </div>

        <div style={{ 
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: '#fff3cd',
          border: '2px solid #ffc107',
          borderRadius: '4px'
        }}>
          <p style={{ 
            color: '#856404', 
            fontWeight: 'bold', 
            marginBottom: '0.75rem',
            fontSize: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            📋 IMPORTANT: Export Data Before Deletion
          </p>
          <p style={{ color: '#856404', margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>
            Before deleting this project, it is <strong>strongly recommended</strong> to export all matrix data for your records:
          </p>
          <ol style={{ color: '#856404', margin: '0.5rem 0 0.5rem 1.5rem', paddingLeft: '0', fontSize: '0.9rem' }}>
            <li>Navigate to the project's matrix page</li>
            <li>Click "Export to Excel"</li>
            <li>Select "<strong>All Database Columns</strong>"</li>
            <li>Save the export file before proceeding with deletion</li>
          </ol>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <p style={{ color: '#dc3545', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            ⚠️ Warning: This action cannot be undone!
          </p>
          <ul style={{ color: '#666', margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
            <li>The project will be permanently deleted from the database</li>
            <li>All associated matrix data will be permanently deleted</li>
            <li>All shop progress trend data will be permanently deleted</li>
            <li>All project assignments will be removed</li>
            <li>This action cannot be reversed</li>
          </ul>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            To confirm deletion, please type "delete" in the box below:
          </label>
          <input
            type="text"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            placeholder="Type 'delete' to confirm"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '1rem',
              boxSizing: 'border-box'
            }}
            disabled={isDeleting}
          />
        </div>

        {error && (
          <div style={{
            padding: '0.75rem',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid #f5c6cb'
          }}>
            {error}
          </div>
        )}

        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={handleCancel}
            disabled={isDeleting}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              opacity: isDeleting ? 0.6 : 1
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting || confirmationText.toLowerCase() !== 'delete'}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: confirmationText.toLowerCase() === 'delete' ? '#dc3545' : '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (isDeleting || confirmationText.toLowerCase() !== 'delete') ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              opacity: (isDeleting || confirmationText.toLowerCase() !== 'delete') ? 0.6 : 1
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete Project'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteProjectModal;
